/**
 * Onglet « Importer un relevé ».
 *
 * Le principe tenu par tout l'écran : **on regarde avant d'écrire**. Déposer
 * des fichiers produit un plan, pas une écriture. Le bouton « Appliquer »
 * n'apparaît qu'ensuite, et il dit exactement combien de lignes il va créer.
 *
 * Le détail par mouvement, dépliable avant d'appliquer, répond à un manque
 * signalé au premier essai : les totaux disaient « 4 rapprochés » sans dire
 * **lesquels**, ni sur quelle facture. Un contrôle qu'on ne peut pas exercer
 * n'est pas un contrôle, et c'est à cet instant précis — avant l'écriture —
 * qu'il coûte le moins cher.
 */

import { useCallback, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileUp, Loader2,
  PlayCircle, RotateCcw, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { commitImport, planImport, planImportCsv, type ImportPlan, type ImportReport } from '../../../lib/payments/import';
import { importGatewaySupabase } from '../../../lib/payments/gateway.supabase';
import type { ManualReason } from '../../../lib/payments/matching';
import { messageErreur } from '../../../lib/payments/erreur';
import { dateCourte, euros } from './money';

interface FichierPrepare {
  filename: string;
  /**
   * Le fichier lui-même, gardé pour pouvoir **reprojeter** juste avant d'écrire.
   *
   * Un plan est une photographie de la base au moment de l'analyse. Quand
   * plusieurs fichiers sont appliqués à la suite, les plans des suivants ont
   * été calculés avant que le premier n'écrive : deux exports qui se recouvrent
   * planifieraient tous deux le même mouvement. La contrainte d'unicité le
   * refuserait — donc aucun doublon en base — mais l'écriture s'arrêterait au
   * milieu, et la moitié d'un import est pire qu'un import à refaire.
   *
   * Mesuré sur les deux exports réels du 18 septembre 2026 : ils se recouvrent
   * sur trois mouvements seulement, tous de montants différents. Le risque est
   * donc petit — et c'est exactement le genre de risque qui se referme une
   * fois, avant qu'il ne se réalise.
   */
  fichier: File;
  plan: ImportPlan | null;
  erreur: string | null;
  rapport: ImportReport | null;
}

/** Projette un fichier, selon ce que son extension annonce. */
async function projeter(f: File): Promise<ImportPlan> {
  const octets = new Uint8Array(await f.arrayBuffer());
  /*
    Le format se lit sur l'extension, et non sur le contenu.

    Un CODA est un fichier de lignes de 128 caractères, un export CSV une table
    à points-virgules : les confondre est impossible en pratique, mais deviner
    plutôt que lire ferait porter à l'utilisateur la conséquence d'une
    heuristique. L'extension est ce qu'il a choisi.
  */
  return /\.csv$/i.test(f.name)
    ? planImportCsv(octets, f.name, importGatewaySupabase)
    : planImport(octets, f.name, importGatewaySupabase);
}

/** Pourquoi un mouvement part en file manuelle, en français. */
const RAISONS: Record<ManualReason, string> = {
  no_reference: 'aucune référence',
  unknown_reference: 'référence inconnue de nos factures',
  foreign_communication: "communication structurée d'un autre système",
  invoice_cancelled: 'la facture citée est annulée',
  invoice_settled: 'la facture citée est déjà réglée',
  invoice_credited: 'la facture citée est entièrement annulée par des notes de crédit',
  surplus: 'surplus au-delà de ce que la facture attendait',
  out_of_scope_pattern: 'forme d\'une activité hors périmètre',
};

export default function CodaImportPanel({ onApplied }: { onApplied: () => void }) {
  const [fichiers, setFichiers] = useState<FichierPrepare[]>([]);
  const [analyse, setAnalyse] = useState(false);
  const [application, setApplication] = useState(false);
  const [deplie, setDeplie] = useState<string | null>(null);

  const analyser = useCallback(async (liste: FileList) => {
    setAnalyse(true);
    setFichiers([]);
    setDeplie(null);
    const prepares: FichierPrepare[] = [];

    for (const f of Array.from(liste)) {
      try {
        prepares.push({ filename: f.name, fichier: f, plan: await projeter(f), erreur: null, rapport: null });
      } catch (e) {
        // Un fichier illisible ne doit pas empêcher de traiter les six autres
        // de la semaine : on note l'erreur et on continue.
        prepares.push({
          filename: f.name, fichier: f, plan: null,
          erreur: messageErreur(e, 'Fichier illisible'), rapport: null,
        });
      }
    }

    setFichiers(prepares);
    setAnalyse(false);
  }, []);

  const appliquer = useCallback(async () => {
    setApplication(true);
    const misAJour = [...fichiers];

    for (let i = 0; i < misAJour.length; i++) {
      const f = misAJour[i];
      if (!f.plan || f.erreur || f.rapport) continue;
      try {
        /* À partir du second, on reprojette : la base a changé depuis
           l'analyse, puisque le précédent vient d'y écrire. Le premier garde le
           plan que vous avez lu à l'écran. */
        const plan = i === 0 ? f.plan : await projeter(f.fichier);
        misAJour[i] = { ...f, plan, rapport: await commitImport(plan, importGatewaySupabase) };
      } catch (e) {
        misAJour[i] = { ...f, erreur: messageErreur(e, 'Écriture refusée') };
      }
      setFichiers([...misAJour]);
    }

    setApplication(false);
    const crees = misAJour.reduce((s, f) => s + (f.rapport?.paymentsCreated ?? 0), 0);
    const affectes = misAJour.reduce((s, f) => s + (f.rapport?.allocationsCreated ?? 0), 0);
    const relies = misAJour.reduce((s, f) => s + (f.rapport?.reunisAuCsv ?? 0), 0);
    toast.success(`${crees} paiement(s) enregistré(s), ${affectes} rapproché(s) automatiquement`
      + (relies > 0 ? `, ${relies} déjà dans l'export CSV (reliés, non réécrits)` : ''));
    onApplied();
  }, [fichiers, onApplied]);

  /*
    Un fichier DEJA IMPORTE reste applicable s'il a des depenses a reprendre.

    Jusqu'au 14 septembre 2026, les virements sortants etaient jetes — 1 712 sur
    1 758 du fichier d'un an. Les fichiers deja importes n'ont donc pas livre
    leurs depenses, et les exclure ici les condamnerait a ne jamais le faire.

    L'empreinte protege les VIREMENTS RECUS : `planImport` n'en met aucun dans
    `payments` sur ces fichiers-la, et rien de ce qui est deja en base ne bouge.
    Depuis le 29/09/2026, il y met en revanche les operations hors virement
    (depot d'especes, remboursement par carte) que l'import jetait, et les
    sorties du meme genre (cartes, frais) : elles aussi se reprennent.
  */
  const aAppliquer = fichiers.filter(
    (f) => f.plan && !f.erreur && !f.rapport
      && (!f.plan.alreadyImportedAs || f.plan.refunds.length > 0 || f.plan.payments.length > 0
        || f.plan.reunisAuCsv.length > 0),
  );
  const totalPaiements = aAppliquer.reduce((s, f) => s + (f.plan?.payments.length ?? 0), 0);
  const totalAllocations = aAppliquer.reduce((s, f) => s + (f.plan?.allocations.length ?? 0), 0);
  const totalDepenses = aAppliquer.reduce((s, f) => s + (f.plan?.refunds.length ?? 0), 0);
  const totalReunis = aAppliquer.reduce((s, f) => s + (f.plan?.reunisAuCsv.length ?? 0), 0);
  const totalProbables = aAppliquer.reduce((s, f) => s + (f.plan?.doublonsProbables.length ?? 0), 0);
  const totalCentimes = aAppliquer.reduce(
    (s, f) => s + (f.plan?.payments.reduce((t, p) => t + p.amount_cents, 0) ?? 0), 0,
  );
  /* Une reprise pure : des fichiers deja importes, qui n'apportent que ce que
     l'import avait jete — virements sortants, cartes, frais, depots. */
  const repriseSeule = aAppliquer.length > 0 && aAppliquer.every((f) => f.plan?.alreadyImportedAs);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-8 text-center">
        <FileUp className="mx-auto h-10 w-10 text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-900">
          Déposez vos fichiers CODA de la semaine
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Les <code className="rounded bg-gray-100 px-1">.BC2</code> téléchargés chez ING, sept par semaine.
          Vous pouvez tous les sélectionner d'un coup.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          Un export <code className="rounded bg-gray-100 px-1">.csv</code> de la banque en ligne est accepté
          aussi : il sert à <strong>combler les trous</strong> quand un relevé manque. Rien n'y est compté
          deux fois — un mouvement déjà connu, qu'il vienne d'un relevé ou d'un export précédent, est
          reconnu et laissé de côté. Et un relevé qui arrive <strong>après</strong> l'export relie ses
          mouvements aux lignes de l'export au lieu de les réécrire.
        </p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
          <FileUp className="h-4 w-4" />
          Choisir des fichiers
          <input
            type="file"
            multiple
            accept=".BC2,.bc2,.txt,.cod,.csv,.CSV"
            className="hidden"
            onChange={(e) => e.target.files?.length && analyser(e.target.files)}
          />
        </label>
        <p className="mt-3 text-xs text-gray-500">
          Rien n'est enregistré à cette étape : vous verrez d'abord ce que l'import ferait.
        </p>
      </div>

      {analyse && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture des fichiers…
        </div>
      )}

      {fichiers.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Fichier</th>
                <th className="px-4 py-2 text-left font-medium">Relevé</th>
                <th className="px-4 py-2 text-right font-medium">Mouvements</th>
                <th className="px-4 py-2 text-right font-medium">Paiements</th>
                <th className="px-4 py-2 text-right font-medium">Rapprochés</th>
                <th className="px-4 py-2 text-right font-medium">Montant</th>
                <th className="px-4 py-2 text-left font-medium">État</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fichiers.map((f) => (
                <LigneFichier
                  key={f.filename}
                  fichier={f}
                  deplie={deplie === f.filename}
                  basculer={() => setDeplie(deplie === f.filename ? null : f.filename)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aAppliquer.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <div className="text-sm text-indigo-900">
            <p className="font-medium">
              {repriseSeule
                ? <>{totalDepenses + totalPaiements} mouvement(s) à reprendre</>
                : <>{totalPaiements} paiement(s) à enregistrer, {euros(totalCentimes)}</>}
            </p>
            <p className="mt-0.5 text-indigo-700">
              {repriseSeule
                ? <>Ces fichiers sont déjà importés : <strong>aucun virement reçu ne sera touché</strong>.
                    Seuls les mouvements qui n'avaient jamais été enregistrés — virements sortants,
                    paiements par carte, frais, dépôts — sont rattrapés, hors périmètre.</>
                : <>dont {totalAllocations} rapproché(s) automatiquement avec une facture.
                    Le reste ira dans la file de rapprochement.
                    {totalDepenses > 0 && <> {totalDepenses} dépense(s) seront aussi enregistrées.</>}</>}
            </p>
            {totalReunis > 0 && (
              <p className="mt-0.5 text-indigo-700">
                {totalReunis} mouvement(s) sont déjà enregistrés par l'export CSV : ils seront{' '}
                <strong>reliés au relevé</strong>, sans être réécrits.
              </p>
            )}
            {totalProbables > 0 && (
              <p className="mt-0.5 font-medium text-amber-800">
                {totalProbables} mouvement(s) ressemblent à une ligne de l'export CSV (même date, même montant,
                même compte) mais disent autre chose : ils seront écrits, et signalés dans la file pour
                que vous vérifiiez que ce n'est pas le même virement.
              </p>
            )}
            <p className="mt-0.5 text-xs text-indigo-700">
              Cliquez le nom d'un fichier pour voir, mouvement par mouvement, ce qui sera
              rapproché et sur quelle facture.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFichiers([])}
              disabled={application}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" /> Annuler
            </button>
            <button
              onClick={appliquer}
              disabled={application}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {application ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              Appliquer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LigneFichier({
  fichier, deplie, basculer,
}: { fichier: FichierPrepare; deplie: boolean; basculer: () => void }) {
  const { plan, erreur, rapport } = fichier;
  const paiements = plan?.payments ?? [];
  const montant = paiements.reduce((s, p) => s + p.amount_cents, 0);

  return (
    <>
      <tr className={clsx(erreur && 'bg-red-50', rapport && 'bg-green-50')}>
        <td className="px-4 py-2">
          <button
            onClick={basculer}
            disabled={!plan}
            className="inline-flex items-center gap-1 font-mono text-xs text-gray-900 hover:text-indigo-700 disabled:cursor-default"
          >
            {plan && (deplie
              ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />)}
            {fichier.filename}
          </button>
        </td>
        <td className="px-4 py-2 text-gray-600">
          {plan ? `n° ${plan.statementNumber} · ${plan.statementDate ?? '—'}` : '—'}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{plan?.movementCount ?? '—'}</td>
        <td className="px-4 py-2 text-right tabular-nums text-gray-900">
          {plan ? paiements.length : '—'}
          {plan && plan.duplicateRefs.length > 0 && (
            <span className="ml-1 text-xs text-gray-500">(+{plan.duplicateRefs.length} déjà connus)</span>
          )}
          {plan && plan.doublonsProbables.length > 0 && (
            <span className="ml-1 text-xs font-medium text-amber-700">
              dont {plan.doublonsProbables.length} à vérifier
            </span>
          )}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-gray-900">{plan?.allocations.length ?? '—'}</td>
        <td className="px-4 py-2 text-right tabular-nums text-gray-900">{plan ? euros(montant) : '—'}</td>
        <td className="px-4 py-2"><Etat fichier={fichier} /></td>
      </tr>

      {deplie && plan && (
        <tr>
          <td colSpan={7} className="bg-gray-50 px-4 py-3">
            <DetailPlan plan={plan} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Ce que l'import ferait, mouvement par mouvement.
 *
 * Les mouvements rapprochés viennent d'abord, parce que ce sont eux qu'on veut
 * vérifier avant d'écrire ; la file manuelle est ensuite listée avec sa raison,
 * ce qui permet de savoir à quoi s'attendre plutôt que de le découvrir.
 */
function DetailPlan({ plan }: { plan: ImportPlan }) {
  const parRef = new Map(plan.payments.map((p) => [p.external_ref, p]));
  const rapproches = plan.matchPlans.filter((m) => m.allocations.length > 0);
  const enFile = plan.matchPlans.filter((m) => m.allocations.length === 0);
  /* Les operations hors virement — cartes, frais, depots —, que l'import
     jetait jusqu'au 29/09/2026 : dites a part, pour qu'on les voie arriver. */
  const horsVirement = new Set(plan.horsVirementAEcrire);
  const sortiesHorsVirement = plan.refunds.filter((r) => horsVirement.has(r.external_ref)).length;
  const entreesHorsVirement = plan.payments.filter((p) => horsVirement.has(p.external_ref)).length;

  return (
    <div className="space-y-4">
      <section>
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Rapprochés automatiquement ({rapproches.length})
        </h4>
        {rapproches.length === 0 ? (
          <p className="mt-1 text-sm text-gray-600">Aucun mouvement de ce fichier ne cite une facture connue.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {rapproches.map((m) => {
              const p = parRef.get(m.externalRef);
              return (
                <li key={m.externalRef} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="w-20 shrink-0 text-xs text-gray-500">{dateCourte(m.valueDate)}</span>
                  <span className="w-24 shrink-0 text-right font-medium tabular-nums text-gray-900">
                    {euros(m.amountCents)}
                  </span>
                  <span className="max-w-[18rem] truncate font-mono text-xs text-gray-600">
                    {p?.communication_raw?.trim() || '—'}
                  </span>
                  <span className="text-gray-400">→</span>
                  {m.allocations.map((a) => (
                    <span key={a.invoiceId} className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-900">
                      <span className="font-mono">{a.invoiceNumber}</span> {euros(a.amountCents)}
                    </span>
                  ))}
                  {m.leftoverCents > 0 && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                      reste {euros(m.leftoverCents)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Ira dans la file de rapprochement ({enFile.length})
        </h4>
        {enFile.length === 0 ? (
          <p className="mt-1 text-sm text-gray-600">Rien : tous les mouvements ont trouvé leur facture.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {enFile.map((m) => {
              const p = parRef.get(m.externalRef);
              return (
                <li key={m.externalRef} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="w-20 shrink-0 text-xs text-gray-500">{dateCourte(m.valueDate)}</span>
                  <span className="w-24 shrink-0 text-right font-medium tabular-nums text-gray-900">
                    {euros(m.amountCents)}
                  </span>
                  <span className="max-w-[18rem] truncate font-mono text-xs text-gray-600">
                    {p?.communication_raw?.trim() || '—'}
                  </span>
                  <span className="max-w-[16rem] truncate text-xs text-gray-500">
                    {p?.counterparty_name ?? ''}
                  </span>
                  <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700">
                    {m.manualReasons.map((r) => RAISONS[r]).join(' · ') || 'à décider'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {plan.reunisAuCsv.length > 0 && (
        <section>
          <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Déjà enregistrés par l'export CSV ({plan.reunisAuCsv.length})
          </h4>
          <p className="mt-1 text-sm text-gray-600">
            Le même virement, lu une première fois dans l'export de la banque en ligne. Il n'est pas
            réécrit : la ligne de l'export reçoit la référence du relevé, et garde ses placements.
          </p>
          <ul className="mt-2 space-y-1.5">
            {plan.reunisAuCsv.map((r) => (
              <li key={r.externalRef} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="w-20 shrink-0 text-xs text-gray-500">{dateCourte(r.valueDate)}</span>
                <span className="w-24 shrink-0 text-right font-medium tabular-nums text-gray-900">
                  {r.sens === 'sortie' ? '−' : ''}{euros(r.amountCents)}
                </span>
                <span className="max-w-[18rem] truncate font-mono text-xs text-gray-600">
                  {r.communication || '—'}
                </span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  {r.communications === 'identiques' ? 'même communication' : 'aucune communication pour les distinguer'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {plan.doublonsProbables.length > 0 && (
        <section>
          <h4 className="text-xs font-medium uppercase tracking-wide text-amber-800">
            À vérifier : peut-être déjà dans l'export CSV ({plan.doublonsProbables.length})
          </h4>
          <p className="mt-1 text-sm text-amber-900">
            Même date, même montant, même compte qu'une ligne de l'export, mais une autre communication.
            Deux virements distincts peuvent se ressembler à ce point : ils sont écrits, et la file les
            signale. Si c'est le même virement, la file permet de réunir les deux lignes.
          </p>
          <ul className="mt-2 space-y-1.5">
            {plan.doublonsProbables.map((d) => (
              <li key={d.externalRef} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="w-20 shrink-0 text-xs text-gray-500">{dateCourte(d.valueDate)}</span>
                <span className="w-24 shrink-0 text-right font-medium tabular-nums text-gray-900">
                  {d.sens === 'sortie' ? '−' : ''}{euros(d.amountCents)}
                </span>
                <span className="text-xs text-gray-600">
                  relevé : <span className="font-mono">{d.communicationReleve || '—'}</span>
                  {' · '}export : <span className="font-mono">{d.communicationCsv || '—'}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {plan.refunds.length > 0 && (
        <p className="text-xs text-gray-600">
          {plan.refunds.length} sortie(s) à enregistrer
          {sortiesHorsVirement > 0 && <>, dont {sortiesHorsVirement} hors virement (cartes, frais…), hors périmètre</>}.
        </p>
      )}
      {entreesHorsVirement > 0 && (
        <p className="text-xs text-gray-600">
          {entreesHorsVirement} entrée(s) hors virement (dépôt, remboursement par carte…) : écrites sans
          rapprochement, visibles dans la file.
        </p>
      )}
      {plan.horsVirementDejaEcrits.length > 0 && (
        <p className="text-xs text-gray-600">
          {plan.horsVirementDejaEcrits.length} opération(s) hors virement déjà écrite(s) par l'export CSV
          (même date, même montant) : pas réécrite(s).
        </p>
      )}
      {plan.soldes && (
        <p className="text-xs text-gray-600">
          Solde de l'extrait : {euros(plan.soldes.ancienCents)} → <strong>{euros(plan.soldes.nouveauCents)}</strong>
        </p>
      )}
    </div>
  );
}

function Etat({ fichier }: { fichier: FichierPrepare }) {
  const { plan, erreur, rapport } = fichier;

  if (erreur) {
    return (
      <span className="inline-flex items-start gap-1.5 text-xs text-red-700">
        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{erreur}</span>
      </span>
    );
  }
  if (rapport) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {/*
          « Déjà importé, rien écrit » était vrai tant qu'un fichier relu ne
          pouvait qu'être compté. Il peut désormais COMPLÉTER : le 20 septembre
          2026, 34 remboursements venus des CODA n'ont ni compte de bénéficiaire,
          ni nom, ni texte libre, et seule une relecture peut les leur rendre.
          Annoncer « rien écrit » après avoir écrit ferait douter de l'écran.
        */}
        {rapport.alreadyImported
          ? [
            rapport.refundsCreated > 0 ? `${rapport.refundsCreated} dépense(s) reprise(s)` : null,
            rapport.paymentsCreated > 0 ? `${rapport.paymentsCreated} entrée(s) hors virement reprise(s)` : null,
            rapport.refundsCompleted > 0 ? `${rapport.refundsCompleted} complétée(s)` : null,
            rapport.reunisAuCsv > 0 ? `${rapport.reunisAuCsv} relié(s) à l'export CSV` : null,
          ].filter(Boolean).join(', ') || 'déjà importé, rien écrit'
          : [
            `${rapport.paymentsCreated} créé(s)`,
            `${rapport.allocationsCreated} rapproché(s)`,
            rapport.reunisAuCsv > 0 ? `${rapport.reunisAuCsv} relié(s) à l'export CSV` : null,
            rapport.refundsCompleted > 0 ? `${rapport.refundsCompleted} dépense(s) complétée(s)` : null,
          ].filter(Boolean).join(', ')}
      </span>
    );
  }
  if (plan?.alreadyImportedAs) {
    return (
      <span className="inline-flex items-start gap-1.5 text-xs text-amber-700">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Déjà importé sous <span className="font-mono">{plan.alreadyImportedAs.filename}</span> —
          {plan.refunds.length === 0 && plan.payments.length === 0
            && plan.refundCompletions.length === 0 && plan.reunisAuCsv.length === 0
            ? ' ce fichier sera ignoré.'
            : ' aucun virement reçu ne sera réécrit, mais il apporte encore'
              + [
                plan.refunds.length > 0 ? ` ${plan.refunds.length} dépense(s)` : '',
                plan.payments.length > 0 ? ` ${plan.payments.length} entrée(s) hors virement` : '',
                plan.refundCompletions.length > 0
                  ? ` ${plan.refundCompletions.length} compte(s) de bénéficiaire` : '',
                plan.reunisAuCsv.length > 0
                  ? ` ${plan.reunisAuCsv.length} lien(s) vers l'export CSV` : '',
              ].filter(Boolean).join(' et') + '.'}
          {/* Un fichier relu se contrôle aussi : un écart ne doit pas se taire
              parce que le fichier est connu. */}
          {plan.problems.length > 0 && <> Contrôle : {plan.problems.join(' · ')}</>}
        </span>
      </span>
    );
  }
  if (plan && plan.problems.length > 0) {
    // Un écart de totaux ne fait pas rejeter le fichier : il est signalé pour
    // qu'on sache qu'il était douteux, plutôt que perdu.
    return (
      <span className="inline-flex items-start gap-1.5 text-xs text-amber-700">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Totaux de contrôle : {plan.problems.join(' · ')}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Prêt, totaux concordants
    </span>
  );
}
